import React, { useEffect, useRef, useState, lazy, Suspense } from 'react';
import ReactDOM from 'react-dom';
import { X, Download, Calendar, Clock, HardDrive, Share, ShieldCheck, User } from 'lucide-react';
import { fmtMonthYear } from '../../utils/educationLifecycle';
import { shareFile } from '../../utils/shareFile';
import { recordOpen } from '../../utils/educationLibrary';
import BookmarkButton from './BookmarkButton';
import EducationVideoPlayer from './EducationVideoPlayer';
import { downloadFile } from '../../utils/downloadUtils';
import { getContentDownloadURL } from '../../utils/educationContent';

// pdf.js is heavy; it arrives only when a document or deck is opened
const PDFViewer = lazy(() => import('./PDFViewer'));

const formatFileSize = (bytes) => {
  if (!bytes) return null;
  const mb = bytes / 1024 / 1024;
  return mb >= 1024 ? `${(mb / 1024).toFixed(1)} GB` : `${mb.toFixed(1)} MB`;
};

const formatDuration = (seconds) => {
  if (!seconds) return null;
  const total = Math.round(seconds); // Mux reports fractional seconds
  const mins = Math.floor(total / 60);
  const secs = total % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

const formatDate = (timestamp) => {
  if (!timestamp) return null;
  const date = timestamp.toDate?.() || new Date(timestamp.seconds ? timestamp.seconds * 1000 : timestamp);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const TYPE_LABEL = { video: 'Video', presentation: 'Presentation', document: 'Document' };

// Pulling the sheet down this far (px) dismisses it
const DISMISS_DRAG_PX = 90;

/**
 * Viewer for Education Center videos and presentations.
 *
 * Phones: a full-width sheet that slides up from the bottom. The media runs
 * edge to edge across the top on a dark ground; title, details and download
 * sit beneath it. Drag the handle down, tap the backdrop, press Escape or the
 * X to close. Tablets and desktops: a centered card with the same layout.
 *
 * `accent` is a Tailwind gradient pair (e.g. "from-emerald-600 to-emerald-800")
 * that carries the topic colour into the category chip.
 */
// Description shows two lines, with a "more" toggle when it runs longer
const ExpandableDescription = ({ text }) => {
  const [expanded, setExpanded] = useState(false);
  const [clamped, setClamped] = useState(false);
  const ref = useRef(null);

  // Only offer "more" when the clamp is actually hiding something
  useEffect(() => {
    const el = ref.current;
    if (!el || expanded) return;
    setClamped(el.scrollHeight > el.clientHeight + 1);
  }, [text, expanded]);

  return (
    <div className="px-4 pt-1">
      <p
        ref={ref}
        className={`text-sm text-gray-600 dark:text-gray-300 leading-relaxed ${expanded ? '' : 'line-clamp-2'}`}
      >
        {text}
      </p>
      {(clamped || expanded) && (
        <button
          onClick={() => setExpanded((v) => !v)}
          className="mt-0.5 text-sm font-medium text-blue-600 dark:text-blue-400 hover:underline"
        >
          {expanded ? 'less' : 'more'}
        </button>
      )}
    </div>
  );
};

const ContentViewer = ({ content, accent = 'from-gray-700 to-gray-900', onClose }) => {
  const [downloading, setDownloading] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState(null);
  const [dragY, setDragY] = useState(0);
  const dragStartY = useRef(null);
  const sheetRef = useRef(null);

  // Escape closes
  useEffect(() => { recordOpen(content); }, [content]);

  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  if (!content) return null;

  const close = () => {
    onClose();
  };

  // Drag-to-dismiss, only from the handle / title area so it never fights the player or scrolling
  const onDragStart = (e) => { dragStartY.current = e.touches[0].clientY; };
  const onDragMove = (e) => {
    if (dragStartY.current === null) return;
    setDragY(Math.max(0, e.touches[0].clientY - dragStartY.current));
  };
  const onDragEnd = () => {
    const shouldClose = dragY > DISMISS_DRAG_PX;
    dragStartY.current = null;
    setDragY(0);
    if (shouldClose) close();
  };

  const handleDownload = async () => {
    setDownloading(true);
    setDownloadProgress(0);
    await downloadFile(
      getContentDownloadURL(content),
      content.originalFileName || content.fileName,
      content.fileType,
      setDownloadProgress
    );
    setTimeout(() => {
      setDownloading(false);
      setDownloadProgress(null);
    }, 1000);
  };

  const downloadURL = getContentDownloadURL(content);
  const reviewed = fmtMonthYear(content.lastReviewed);
  const details = [
    { icon: User, text: content.presenter || [content.owner, ...(content.coOwners || [])].map((o) => o && o.name).filter(Boolean).join(', ') },
    { icon: Calendar, text: formatDate(content.uploadDate) },
    { icon: ShieldCheck, text: reviewed ? `Reviewed ${reviewed}` : '' },
    { icon: Clock, text: formatDuration(content.duration) },
    { icon: HardDrive, text: formatFileSize(content.fileSize) },
  ].filter((d) => d.text);

  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 z-50 bg-black/75 dark:bg-black/90 backdrop-blur-sm flex items-end sm:items-center justify-center sm:p-4 animate-fadeIn"
      onClick={close}
    >
      <div
        ref={sheetRef}
        role="dialog"
        aria-modal="true"
        aria-label={content.title}
        onClick={(e) => e.stopPropagation()}
        style={dragY ? { transform: `translateY(${dragY}px)`, transition: 'none' } : undefined}
        className="w-full sm:max-w-3xl max-h-[92vh] sm:max-h-[90vh] flex flex-col bg-white dark:bg-gray-900 rounded-t-3xl sm:rounded-2xl overflow-hidden shadow-[0_-8px_40px_rgba(0,0,0,0.35)] sm:shadow-[0_20px_60px_rgba(0,0,0,0.4)] animate-sheetIn sm:animate-scaleIn transition-transform duration-200 ease-out"
      >
        {/* Media on a dark ground, edge to edge */}
        <div
          className="bg-black flex-shrink-0"
          onTouchStart={onDragStart}
          onTouchMove={onDragMove}
          onTouchEnd={onDragEnd}
        >
          <div className="sm:hidden flex justify-center pt-2.5 pb-2">
            <div className="w-10 h-1 rounded-full bg-white/40" />
          </div>
          {content.fileType === 'video' && <EducationVideoPlayer content={content} frame="bare" />}
        </div>

        {/* Everything below the media scrolls if it has to */}
        <div className="overflow-y-auto pb-[max(1rem,env(safe-area-inset-bottom))]">
          <div
            className="px-4 pt-3 pb-1 flex items-start gap-3"
            onTouchStart={onDragStart}
            onTouchMove={onDragMove}
            onTouchEnd={onDragEnd}
          >
            <div className="flex-1 min-w-0">
              <h2 className="text-lg font-bold leading-snug tracking-tight text-gray-900 dark:text-white">
                {content.title}
              </h2>
              {(content.category || content.subcategory) && (
                <span className={`inline-block mt-1.5 px-2 py-0.5 rounded-full text-[11px] font-medium text-white bg-gradient-to-r ${accent}`}>
                  {[content.category, content.subcategory].filter(Boolean).join(' · ')}
                </span>
              )}
            </div>
            <BookmarkButton item={content} className="-mt-1 flex-shrink-0" size={20} />
            <button
              onClick={close}
              aria-label="Close"
              className="-mr-2 -mt-1 p-2 rounded-full text-gray-500 hover:text-gray-900 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-white dark:hover:bg-gray-800 transition-colors flex-shrink-0"
            >
              <X size={22} />
            </button>
          </div>

          {content.description && <ExpandableDescription text={content.description} />}

          {content.fileType === 'presentation' && (
            <div className="px-4 pt-3">
              <Suspense fallback={<div className="w-full aspect-video bg-gray-100 dark:bg-gray-800 animate-pulse rounded-xl" />}>
                <PDFViewer url={content.downloadURL.replace(/\.(ppt|pptx)(\?|$)/i, '_pdf.pdf$2')} />
              </Suspense>
            </div>
          )}

          {(details.length > 0 || downloadURL) && (
            <div className="mx-4 mt-3 pt-3 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-x-4 gap-y-1 flex-wrap text-xs text-gray-500 dark:text-gray-400">
                {details.map(({ icon: Icon, text }) => (
                  <span key={text} className="inline-flex items-center gap-1">
                    <Icon size={13} />
                    {text}
                  </span>
                ))}
              </div>
              {downloadURL && content.fileType !== 'video' && (
                <button
                  onClick={async () => { setSharing(true); await shareFile(content.fileType === 'presentation' ? downloadURL.replace(/\.(ppt|pptx)(\?|$)/i, '_pdf.pdf$2') : downloadURL, content.title); setSharing(false); }}
                  disabled={sharing}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors disabled:opacity-60"
                >
                  <Share size={15} className={sharing ? 'animate-pulse' : ''} />
                  Share
                </button>
              )}
              {downloadURL && (
                <button
                  onClick={handleDownload}
                  disabled={downloading}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors disabled:opacity-60"
                >
                  <Download size={15} className={downloading ? 'animate-bounce' : ''} />
                  {downloading ? `${downloadProgress ?? 0}%` : `Download ${TYPE_LABEL[content.fileType] || 'File'}`}
                </button>
              )}
            </div>
          )}

          {downloading && downloadProgress !== null && (
            <div className="mx-4 mt-3 h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
              <div
                className="h-full bg-blue-600 rounded-full transition-all duration-300 ease-out"
                style={{ width: `${downloadProgress}%` }}
              />
            </div>
          )}

          {content.tags?.length > 0 && (
            <div className="px-4 mt-3 flex flex-wrap gap-1.5">
              {content.tags.map((tag) => (
                <span key={tag} className="px-2.5 py-0.5 bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 text-xs rounded-full">
                  {tag}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>

      <style>{`
        @keyframes viewerFadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes viewerSheetIn { from { transform: translateY(100%); } to { transform: translateY(0); } }
        @keyframes viewerScaleIn { from { opacity: 0; transform: scale(0.96); } to { opacity: 1; transform: scale(1); } }
        .animate-fadeIn { animation: viewerFadeIn 0.2s ease-out; }
        .animate-sheetIn { animation: viewerSheetIn 0.32s cubic-bezier(0.32, 0.72, 0, 1); }
        @media (min-width: 640px) {
          .sm\\:animate-scaleIn { animation: viewerScaleIn 0.25s ease-out; }
        }
      `}</style>
    </div>,
    document.body
  );
};

export default ContentViewer;
